import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from '@angular/cdk/overlay';
import {
  AfterRenderRef,
  DestroyRef,
  Directive,
  ElementRef,
  Injector,
  OnInit,
  afterEveryRender,
  booleanAttribute,
  inject,
  input,
  output,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {OVERLAY_GAP, OVERLAY_POSITIONS, OVERLAY_VIEWPORT_MARGIN, offsetPositions} from './overlay.constants';
import {repositionOnScroll} from './reposition-on-scroll.strategy';

/**
 * The plumbing every connected panel in the app repeats, applied alongside `cdkConnectedOverlay`
 * on the same `<ng-template>`. It owns the things that were identical at every call site — the
 * scroll strategy, the dismissing transparent backdrop, the house positions standing
 * {@link OVERLAY_GAP} off the trigger and {@link OVERLAY_VIEWPORT_MARGIN} off the window edge,
 * matching the panel to its trigger's width, and keeping the panel on a trigger that moves —
 * leaving each site to declare only what actually differs: the origin, the open state and the
 * content.
 *
 * ```html
 * <ng-template cdkConnectedOverlay appOverlayPanel
 *   [cdkConnectedOverlayOrigin]="trigger"
 *   [cdkConnectedOverlayOpen]="open()"
 *   matchTriggerWidth
 *   (panelClose)="open.set(false)">
 * ```
 *
 * Inputs are read once in `ngOnInit`, which is early enough: the directive configures the CDK
 * directive's plain properties, and those are read when the overlay attaches. {@link panelPositions}
 * therefore has to be static per call site, which every consumer is.
 */
@Directive({
  selector: '[appOverlayPanel]',
})
export class OverlayPanelDirective implements OnInit {
  /**
   * Hold the panel to at least the trigger's width. A minimum rather than a fixed width, so a
   * panel whose content is wider than the control still gets the room it needs — and kept in step
   * with a `ResizeObserver`, because a trigger that labels the current selection changes width
   * *while* the panel is open.
   */
  readonly matchTriggerWidth = input(false, {transform: booleanAttribute});
  /**
   * The dismissing backdrop. On for anything menu-like; off for a panel that must not swallow the
   * next click, such as a typeahead list the user keeps typing underneath.
   */
  readonly backdrop = input(true, {transform: booleanAttribute});
  /**
   * Overrides {@link OVERLAY_POSITIONS} for a panel that needs geometry of its own. The
   * {@link OVERLAY_GAP} is applied to whatever is passed, so a consumer's own positions stand off
   * the trigger too.
   */
  readonly panelPositions = input<ConnectedPosition[]>(OVERLAY_POSITIONS);

  /** The panel wants to close: the backdrop was clicked, or the overlay detached. */
  readonly panelClose = output<void>();

  private readonly overlay = inject(CdkConnectedOverlay);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  /** One strategy per overlay — it holds the single `OverlayRef` it was attached to. */
  private readonly scrollStrategy = repositionOnScroll();

  private triggerResize: ResizeObserver | null = null;
  /** Re-places the open panel after every render; `null` while closed. */
  private followTrigger: AfterRenderRef | null = null;
  /** Last width written to the pane; guards the observer's initial and repeat emissions. */
  private appliedWidth: number | null = null;
  /** Set once this open has asked to close, so the two sources below emit `panelClose` once. */
  private closing = false;

  constructor() {
    // A backdrop click closes by way of the consumer, whose `open` binding then detaches the
    // overlay — so both sources fire for one dismissal. Consumers get one event either way.
    this.overlay.backdropClick.pipe(takeUntilDestroyed()).subscribe(() => this.emitClose());
    this.overlay.detach.pipe(takeUntilDestroyed()).subscribe(() => {
      this.stopMatchingWidth();
      this.stopFollowingTrigger();
      this.emitClose();
    });
    this.overlay.attach.pipe(takeUntilDestroyed()).subscribe(() => {
      this.closing = false;
      this.startMatchingWidth();
      this.startFollowingTrigger();
    });
    this.destroyRef.onDestroy(() => {
      this.stopMatchingWidth();
      this.stopFollowingTrigger();
    });
  }

  ngOnInit(): void {
    this.overlay.scrollStrategy = this.scrollStrategy;
    this.overlay.hasBackdrop = this.backdrop();
    this.overlay.backdropClass = 'cdk-overlay-transparent-backdrop';
    this.overlay.positions = offsetPositions(this.panelPositions(), OVERLAY_GAP);
    this.overlay.viewportMargin = OVERLAY_VIEWPORT_MARGIN;
  }

  private emitClose(): void {
    if (this.closing) {
      return;
    }
    this.closing = true;
    this.panelClose.emit();
  }

  private startMatchingWidth(): void {
    const trigger = this.originElement();
    if (!this.matchTriggerWidth() || !trigger) {
      return;
    }
    // Measured outright, so the width does not depend on `ResizeObserver` being available.
    this.applyWidth(trigger.offsetWidth);
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    // Then followed, for a trigger that labels the current selection and so changes width under
    // an open panel. The observer's initial emission repeats the measurement above, which the
    // unchanged-width guard drops.
    this.triggerResize = new ResizeObserver(() => this.applyWidth(trigger.offsetWidth));
    this.triggerResize.observe(trigger);
  }

  /**
   * CDK re-places a pane on scroll and window resize, not when its trigger moves because the layout
   * around it did — a popup that re-centres as a message appears under the field, say — and the panel
   * would drift off its trigger, the gap no longer {@link OVERLAY_GAP}. Re-placing after every render
   * while open follows such moves; it is one measurement of the trigger per render, and only for an
   * open panel.
   */
  private startFollowingTrigger(): void {
    this.stopFollowingTrigger();
    this.followTrigger = afterEveryRender(
      {mixedReadWrite: () => this.overlay.overlayRef?.updatePosition()},
      {injector: this.injector},
    );
  }

  private stopFollowingTrigger(): void {
    this.followTrigger?.destroy();
    this.followTrigger = null;
  }

  private stopMatchingWidth(): void {
    this.triggerResize?.disconnect();
    this.triggerResize = null;
    this.appliedWidth = null;
  }

  private applyWidth(width: number): void {
    if (width === this.appliedWidth) {
      return;
    }
    this.appliedWidth = width;
    this.overlay.overlayRef.updateSize({minWidth: width});
    // Sizing alone does not move the pane, and an end-aligned panel has to follow the edge that
    // just moved.
    this.overlay.overlayRef.updatePosition();
  }

  /** The trigger's element, whichever of the origin forms `cdkConnectedOverlay` accepts was bound. */
  private originElement(): HTMLElement | null {
    const origin = this.overlay.origin;
    if (origin instanceof CdkOverlayOrigin) {
      return origin.elementRef.nativeElement;
    }
    if (origin instanceof ElementRef) {
      return origin.nativeElement;
    }
    return origin instanceof HTMLElement ? origin : null;
  }
}
